import React, { useState, ChangeEvent, FormEvent, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import Logo from "../../assets/Logo.svg";
import { BaseURL } from "../../components/utils/url";
import { slideFromRight } from "../../animations/Animations";
import { Eye, EyeOff } from "lucide-react";
import { toast } from "react-toastify";

const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState<string>("");
  const [newPassword, setNewPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [step, setStep] = useState<"email" | "password">("email");
  const [showNewPassword, setShowNewPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] =
    useState<boolean>(false);
  const [passwordsMatch, setPasswordsMatch] = useState<boolean | null>(null);

  const navigate = useNavigate();

  useEffect(() => {
    if (confirmPassword.length > 0) {
      setPasswordsMatch(newPassword === confirmPassword);
    } else {
      setPasswordsMatch(null);
    }
  }, [newPassword, confirmPassword]);

  const handleEmailChange = (e: ChangeEvent<HTMLInputElement>) => {
    setError("");
    setEmail(e.target.value.trim());
  };

  const handlePasswordChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setError("");

    if (name === "newPassword") {
      setNewPassword(value);
      if (confirmPassword) {
        setPasswordsMatch(value === confirmPassword);
      }
    }

    if (name === "confirmPassword") {
      setConfirmPassword(value);
      setPasswordsMatch(newPassword === value);
    }
  };

  const handleEmailSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError("");

    if (!email) {
      setError("Please enter your email address");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError("Please enter a valid email address");
      return;
    }

    setLoading(true);

    try {
      // Check if user exists
      const response = await BaseURL.post("/super-admin/forgot-password", {
        email: email,
      });

      if (response.status === 200) {
        // Store email for password reset step
        localStorage.setItem("resetEmail", email);
        setStep("password");
      } else {
        setError("Invalid credentials");
      }
    } catch (err: any) {
      if (err.response?.status === 401) {
        setError("Invalid credentials");
      } else {
        setError("An error occurred. Please try again.");
      }
      // Silently handle error without console logging
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError("");

    if (!newPassword || !confirmPassword) {
      setError("Please fill in all password fields");
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    // Password strength validation
    if (newPassword.length < 8) {
      setError("Password must be at least 8 characters long");
      return;
    }

    setLoading(true);

    try {
      const storedEmail = localStorage.getItem("resetEmail");

      // Send new password to endpoint
      await BaseURL.post("/super-admin/reset-password", {
        email: storedEmail,
        newPassword: newPassword,
      });

      toast.success("Password reset successfully. Redirecting to login");
      // Clear stored email
      localStorage.removeItem("resetEmail");
      // Redirect to login
      setTimeout(() => {
        navigate("/administrator/login");
      }, 2000);
    } catch (err: any) {
      setError("Failed to reset password. Please try again.");
      // Silently handle error without console logging
    } finally {
      setLoading(false);
    }
  };

  // Loading animation variants
  const loadingDotVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        repeat: Infinity,
        repeatType: "reverse" as const,
        duration: 0.5,
      },
    },
  };

  const buttonSpinnerVariants = {
    hidden: { opacity: 0, scale: 0.5 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.5,
        repeat: Infinity,
      },
    },
  };

  const getPasswordMatchIndicator = () => {
    if (passwordsMatch === null) return null;

    return (
      <motion.div
        initial={{ opacity: 0, y: -5 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 5 }}
        className={`text-sm ${
          passwordsMatch ? "text-green-500" : "text-red-500"
        } mt-1`}
      >
        {passwordsMatch ? "Passwords match ✓" : "Passwords don't match ✗"}
      </motion.div>
    );
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 sm:px-6 lg:px-8">
      <div className="w-full max-w-6xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
          <motion.div
            className="w-full max-w-md mx-auto"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="relative">
              <motion.div
                className="absolute inset-0 bg-blue-500 shadow-lg transform -skew-y-6 sm:skew-y-0 sm:-rotate-6 sm:rounded-3xl"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
              />
              <motion.div
                className="relative bg-white shadow-lg sm:rounded-3xl px-4 py-10 sm:p-20"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <div>
                  <h1 className="text-2xl font-semibold">
                    {step === "email" ? "Forgot Password" : "Reset Password"}
                  </h1>
                  <p className="mt-2 text-gray-600 text-sm">
                    {step === "email"
                      ? "Enter your email address to reset your password"
                      : "Enter your new password"}
                  </p>
                </div>
                <div className="divide-y divide-gray-200">
                  <AnimatePresence mode="wait">
                    {error && (
                      <motion.div
                        className="text-red-500 text-sm mt-4"
                        initial={{ opacity: 0, y: -5 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 5 }}
                        transition={{ duration: 0.3 }}
                      >
                        {error}
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {step === "email" ? (
                    <form
                      onSubmit={handleEmailSubmit}
                      className="py-8 space-y-4"
                    >
                      <div className="relative">
                        <input
                          autoComplete="email"
                          id="email"
                          name="email"
                          type="email"
                          className="peer placeholder-transparent h-10 w-full border-b-2 border-gray-300 text-gray-900 focus:outline-none focus:borer-rose-600"
                          placeholder="Email"
                          value={email}
                          onChange={handleEmailChange}
                          disabled={loading}
                        />
                        <label
                          htmlFor="email"
                          className="absolute left-0 -top-3.5 text-gray-600 text-sm peer-placeholder-shown:text-base peer-placeholder-shown:text-gray-440 peer-placeholder-shown:top-2 transition-all peer-focus:-top-3.5 peer-focus:text-gray-600 peer-focus:text-sm"
                        >
                          Email
                        </label>
                      </div>
                      <div className="relative flex flex-col sm:flex-row gap-4">
                        <button
                          type="submit"
                          className="bg-black text-white rounded-md px-4 py-2 w-full sm:w-auto disabled:opacity-80 relative"
                          disabled={loading}
                        >
                          {loading ? (
                            <span className="flex items-center justify-center">
                              <span className="mr-2">Checking</span>
                              <span className="flex space-x-1">
                                <motion.span
                                  className="inline-block h-1.5 w-1.5 bg-white rounded-full"
                                  variants={loadingDotVariants}
                                  initial="hidden"
                                  animate="visible"
                                  transition={{ delay: 0 }}
                                />
                                <motion.span
                                  className="inline-block h-1.5 w-1.5 bg-white rounded-full"
                                  variants={loadingDotVariants}
                                  initial="hidden"
                                  animate="visible"
                                  transition={{ delay: 0.2 }}
                                />
                                <motion.span
                                  className="inline-block h-1.5 w-1.5 bg-white rounded-full"
                                  variants={loadingDotVariants}
                                  initial="hidden"
                                  animate="visible"
                                  transition={{ delay: 0.4 }}
                                />
                              </span>
                            </span>
                          ) : (
                            "Continue"
                          )}
                        </button>
                        <button
                          type="button"
                          onClick={() => navigate("/administrator/login")}
                          className="text-gray-600 hover:text-gray-800 text-sm self-center"
                          disabled={loading}
                        >
                          Back to Login
                        </button>
                      </div>
                    </form>
                  ) : (
                    <form
                      onSubmit={handlePasswordSubmit}
                      className="py-8 space-y-4"
                    >
                      {/* New Password Field with Eye Toggle */}
                      <div className="relative">
                        <div className="flex items-center relative">
                          <input
                            id="newPassword"
                            name="newPassword"
                            type={showNewPassword ? "text" : "password"}
                            className="peer placeholder-transparent h-10 w-full border-b-2 border-gray-300 text-gray-900 focus:outline-none focus:borer-rose-600 pr-10"
                            placeholder="New Password"
                            value={newPassword}
                            onChange={handlePasswordChange}
                            disabled={loading}
                            minLength={8}
                          />
                          <button
                            type="button"
                            onClick={() => setShowNewPassword(!showNewPassword)}
                            className="absolute right-2 focus:outline-none text-gray-500"
                            tabIndex={-1}
                          >
                            {showNewPassword ? (
                              <EyeOff size={18} className="text-gray-500" />
                            ) : (
                              <Eye size={18} className="text-gray-500" />
                            )}
                          </button>
                        </div>
                        <label
                          htmlFor="newPassword"
                          className="absolute left-0 -top-3.5 text-gray-600 text-sm peer-placeholder-shown:text-base peer-placeholder-shown:text-gray-440 peer-placeholder-shown:top-2 transition-all peer-focus:-top-3.5 peer-focus:text-gray-600 peer-focus:text-sm"
                        >
                          New Password
                        </label>
                        {newPassword && (
                          <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            className="text-xs text-gray-500 mt-1"
                          >
                            {newPassword.length < 8 ? (
                              <span className="text-amber-500">
                                Password must be at least 8 characters
                              </span>
                            ) : (
                              <span className="text-green-500">
                                Password strength: Good
                              </span>
                            )}
                          </motion.div>
                        )}
                      </div>

                      {/* Confirm Password Field with Eye Toggle */}
                      <div className="relative">
                        <div className="flex items-center relative">
                          <input
                            id="confirmPassword"
                            name="confirmPassword"
                            type={showConfirmPassword ? "text" : "password"}
                            className={`peer placeholder-transparent h-10 w-full border-b-2 ${
                              passwordsMatch === false
                                ? "border-red-300"
                                : "border-gray-300"
                            } text-gray-900 focus:outline-none focus:borer-rose-600 pr-10`}
                            placeholder="Confirm Password"
                            value={confirmPassword}
                            onChange={handlePasswordChange}
                            disabled={loading}
                          />
                          <button
                            type="button"
                            onClick={() =>
                              setShowConfirmPassword(!showConfirmPassword)
                            }
                            className="absolute right-2 focus:outline-none text-gray-500"
                            tabIndex={-1}
                          >
                            {showConfirmPassword ? (
                              <EyeOff size={18} className="text-gray-500" />
                            ) : (
                              <Eye size={18} className="text-gray-500" />
                            )}
                          </button>
                        </div>
                        <label
                          htmlFor="confirmPassword"
                          className="absolute left-0 -top-3.5 text-gray-600 text-sm peer-placeholder-shown:text-base peer-placeholder-shown:text-gray-440 peer-placeholder-shown:top-2 transition-all peer-focus:-top-3.5 peer-focus:text-gray-600 peer-focus:text-sm"
                        >
                          Confirm Password
                        </label>
                        <AnimatePresence>
                          {confirmPassword && getPasswordMatchIndicator()}
                        </AnimatePresence>
                      </div>

                      <div className="relative flex flex-col sm:flex-row gap-4 mt-8">
                        <button
                          type="submit"
                          className="bg-black text-white rounded-md px-4 py-2 w-full sm:w-auto disabled:opacity-80 relative overflow-hidden"
                          disabled={
                            loading || !passwordsMatch || newPassword.length < 8
                          }
                        >
                          {loading ? (
                            <div className="flex items-center justify-center">
                              <span className="mr-2">Resetting</span>
                              <motion.div
                                className="flex space-x-1"
                                initial="hidden"
                                animate="visible"
                              >
                                <motion.span
                                  className="inline-block h-1.5 w-1.5 bg-white rounded-full"
                                  variants={loadingDotVariants}
                                  transition={{ delay: 0 }}
                                />
                                <motion.span
                                  className="inline-block h-1.5 w-1.5 bg-white rounded-full"
                                  variants={loadingDotVariants}
                                  transition={{ delay: 0.2 }}
                                />
                                <motion.span
                                  className="inline-block h-1.5 w-1.5 bg-white rounded-full"
                                  variants={loadingDotVariants}
                                  transition={{ delay: 0.4 }}
                                />
                              </motion.div>
                            </div>
                          ) : (
                            "Reset Password"
                          )}
                          {loading && (
                            <motion.div
                              className="absolute inset-0 bg-gray-800 bg-opacity-10"
                              initial={{ width: 0 }}
                              animate={{ width: "100%" }}
                              transition={{ duration: 2, ease: "linear" }}
                            />
                          )}
                        </button>
                        <button
                          type="button"
                          onClick={() => setStep("email")}
                          className="text-gray-600 hover:text-gray-800 text-sm self-center"
                          disabled={loading}
                        >
                          Back
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              </motion.div>
            </div>
          </motion.div>

          <motion.div
            className="flex justify-center items-center order-first lg:order-last"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <motion.img
              src={Logo}
              alt="syncai logo"
              initial="hidden"
              animate="visible"
              variants={slideFromRight}
            />
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
